import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { updateArtifact } from '@/client/sdk.gen'
import type { ArtifactResponse } from '@/client/types.gen'
import { FileLabelSelector } from '@/components/project/analysis-modal/FileLabelSelector'
import type { FileLabel, FileLabelSuggestion } from '@/components/project/analysis-modal/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MAX_ARTIFACT_NAME_LENGTH } from '@/constants/artifacts'
import { useTranslation } from '@/hooks/useTranslation'
import { extractErrorMessage, isErrorHandledByInterceptor } from '@/utils/error'

function artifactToFileLabel(artifact: ArtifactResponse): FileLabel {
	return {
		timeDimension: (artifact.time_dimension as 'as_is' | 'to_be' | 'other') || undefined,
		purpose: artifact.purpose ?? undefined,
		purposeCustom: artifact.purpose_custom ?? undefined,
	}
}

export interface ArtifactEditModalProps {
	isOpen: boolean
	onClose: () => void
	artifact: ArtifactResponse | null
	projectId: string | undefined
	onSuccess: () => void
}

export function ArtifactEditModal({
	isOpen,
	onClose,
	artifact,
	projectId,
	onSuccess,
}: ArtifactEditModalProps) {
	const { t } = useTranslation()
	const [name, setName] = useState('')
	const [label, setLabel] = useState<FileLabel>({})
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (artifact) {
			setName(artifact.name)
			setLabel(artifactToFileLabel(artifact))
		}
	}, [artifact])

	if (!isOpen || !artifact || !projectId) return null
	if (typeof document === 'undefined') return null

	// Only offer a live suggestion while classification_source is still pending
	// (`auto`). suggested_* columns are retained after confirm/override for the
	// override-rate metric — do not clear them on save, and do not surface them
	// here once the user has already decided.
	const suggestion: FileLabelSuggestion | undefined =
		artifact.classification_source === 'auto' &&
		(artifact.suggested_purpose || artifact.suggested_time_dimension)
			? {
					timeDimension: artifact.suggested_time_dimension ?? undefined,
					purpose: artifact.suggested_purpose ?? undefined,
					confidence: artifact.classification_confidence ?? undefined,
					rationale: artifact.classification_rationale ?? undefined,
				}
			: undefined

	const handleSave = async () => {
		setSaving(true)
		try {
			const body: {
				name: string
				time_dimension?: string | null
				purpose?: string | null
				purpose_custom?: string | null
			} = {
				name: (name.trim() || artifact.name).slice(0, MAX_ARTIFACT_NAME_LENGTH),
			}
			// Always send label fields so they can be cleared (null)
			body.time_dimension = label.timeDimension ?? null
			body.purpose = label.purpose ?? null
			body.purpose_custom =
				label.purpose === 'other_custom' ? (label.purposeCustom ?? '').trim() || null : null

			const response = await updateArtifact({
				path: { project_id: projectId, artifact_id: artifact.id },
				body,
			})
			if (response.error) {
				if (isErrorHandledByInterceptor(response.error)) return
				toast.error(extractErrorMessage(response.error, 'Failed to save'))
				return
			}
			onSuccess()
			onClose()
		} catch (err) {
			if (isErrorHandledByInterceptor(err)) return
			toast.error(extractErrorMessage(err, 'Failed to save'))
		} finally {
			setSaving(false)
		}
	}

	const modalContent = (
		<div
			className="fixed inset-0 flex items-center justify-center bg-black/30 z-[110]"
			onKeyDown={(e) => e.key === 'Escape' && !saving && onClose()}
			role="dialog"
			aria-modal="true"
			aria-labelledby="artifact-edit-title"
		>
			<div className="bg-background rounded-2xl ring-1 ring-foreground/10 p-5 max-w-lg w-full mx-4 shadow-lg">
				<h2 id="artifact-edit-title" className="text-lg font-semibold mb-1">
					{t('pages.projectDetail.artifactPreview.editModalTitle') as string}
				</h2>
				<p className="text-sm text-muted-foreground mb-4">
					{t('pages.projectDetail.artifactPreview.editModalDescription') as string}
				</p>

				<div className="mb-4 space-y-2">
					<Label htmlFor="artifact-edit-name">
						{t('pages.projectDetail.uploadClassification.artifactName') as string}
					</Label>
					<Input
						id="artifact-edit-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={artifact.name}
						className="font-medium"
						maxLength={255}
					/>
				</div>

				<FileLabelSelector
					label={label}
					onChange={setLabel}
					isContextFile
					variant="plain"
					suggestion={suggestion}
				/>

				<div className="flex gap-2 mt-6">
					<Button type="button" variant="outline" onClick={onClose} disabled={saving} className="flex-1">
						{t('pages.projectDetail.uploadClassification.cancel') as string}
					</Button>
					<Button
						type="button"
						onClick={handleSave}
						disabled={saving || !label.timeDimension}
						className="flex-1"
					>
						{saving
							? (t('pages.projectDetail.artifactPreview.saving') as string)
							: (t('pages.projectDetail.artifactPreview.save') as string)}
					</Button>
				</div>
			</div>
		</div>
	)

	return createPortal(modalContent, document.body)
}
